/* Presentation only. Purchases, cart rendering, search and account stay in Horizon. */
(() => {
  if (window.earthImanInitialized) return;
  window.earthImanInitialized = true;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const controllers = new WeakMap();
  const observers = new WeakMap();
  const readSaved = () => {
    try { const values = JSON.parse(localStorage.getItem('earth-iman-saved') || '[]'); return new Set(Array.isArray(values) ? values : []); }
    catch { return new Set(); }
  };
  let saved = readSaved();
  const syncSaved = () => document.querySelectorAll('[data-ei-save]').forEach(button => {
    const active = saved.has(button.dataset.eiSave);
    button.setAttribute('aria-pressed', String(active));
    const title = button.getAttribute('aria-label').replace(/^(Save|Unsave) /, '');
    button.setAttribute('aria-label', `${active ? 'Unsave' : 'Save'} ${title}`);
  });
  function selectTab(root, button, focus = false) {
    const tabs = [...root.querySelectorAll('[role="tab"]')];
    const index = tabs.indexOf(button);
    tabs.forEach((tab, i) => {
      tab.setAttribute('aria-selected', String(i === index));
      tab.tabIndex = i === index ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = i !== index;
    });
    root.querySelectorAll('[data-ei-ritual-purchase]').forEach((purchase, i) => { purchase.hidden = i !== index; });
    if (focus) button.focus();
  }
  function init(container = document) {
    container.querySelectorAll('[data-ei-tabs], [data-ei-ritual]').forEach(root => {
      if (controllers.has(root)) return;
      const controller = new AbortController();
      controllers.set(root, controller);
      const options = { signal: controller.signal };
      root.addEventListener('click', event => {
        const button = event.target.closest('[role="tab"]');
        if (button) selectTab(root, button);
      }, options);
      root.addEventListener('keydown', event => {
        const button = event.target.closest('[role="tab"]');
        if (!button) return;
        const tabs = [...root.querySelectorAll('[role="tab"]')];
        let i = tabs.indexOf(button);
        const vertical = button.closest('[role="tablist"]').getAttribute('aria-orientation') === 'vertical';
        if (event.key === (vertical ? 'ArrowDown' : 'ArrowRight')) i++;
        else if (event.key === (vertical ? 'ArrowUp' : 'ArrowLeft')) i--;
        else if (event.key === 'Home') i = 0;
        else if (event.key === 'End') i = tabs.length - 1;
        else return;
        event.preventDefault();
        selectTab(root, tabs[(i + tabs.length) % tabs.length], true);
      }, options);
    });
    container.querySelectorAll('[data-ei-header]').forEach(header => {
      if (controllers.has(header)) return;
      const controller = new AbortController();
      controllers.set(header, controller);
      const options = { signal: controller.signal };
      const wrapper = document.querySelector('.page-wrapper');
      const onScroll = () => header.classList.toggle('ei-scrolled', Math.max(window.scrollY, wrapper?.scrollTop || 0) > 24);
      window.addEventListener('scroll', onScroll, { ...options, passive: true });
      wrapper?.addEventListener('scroll', onScroll, { ...options, passive: true });
      onScroll();
      const dialog = header.querySelector('.ei-mobile-menu');
      const trigger = header.querySelector('[data-ei-menu-open]');
      const close = () => {
        trigger.setAttribute('aria-expanded', 'false');
        dialog.close();
      };
      trigger.addEventListener('click', () => {
        dialog.showModal();
        trigger.setAttribute('aria-expanded', 'true');
        document.documentElement.setAttribute('scroll-lock', '');
      }, options);
      dialog.querySelector('[data-ei-menu-close]').addEventListener('click', close, options);
      dialog.addEventListener('cancel', event => { event.preventDefault(); close(); }, options);
      dialog.addEventListener('click', event => {
        const bounds = dialog.getBoundingClientRect();
        if (event.target === dialog && (event.clientX > bounds.right || event.clientX < bounds.left)) close();
        if (event.target.closest('a')) close();
      }, options);
      dialog.addEventListener('close', () => {
        trigger.setAttribute('aria-expanded', 'false');
        navItems.forEach(item => closeNavItem(item));
        if (!document.querySelector('dialog[open], theme-drawer[open]')) document.documentElement.removeAttribute('scroll-lock');
        trigger.focus({ preventScroll: true });
      }, options);
      window.addEventListener('resize', () => {
        closeNavItems();
        if (window.innerWidth >= 1024 && dialog.open) close();
      }, options);
      const navItems = [...header.querySelectorAll('[data-ei-nav-item]')];
      let skipFocusOpen = null;
      const closeNavItem = (item, restoreFocus = false) => {
        const toggle = item.querySelector('[data-ei-submenu-toggle]');
        const submenu = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
        if (!toggle || !submenu) return;
        item.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        submenu.hidden = true;
        if (restoreFocus) {
          skipFocusOpen = item;
          toggle.focus({ preventScroll: true });
          queueMicrotask(() => { if (skipFocusOpen === item) skipFocusOpen = null; });
        }
      };
      const closeNavItems = except => navItems.forEach(item => {
        if (item !== except) closeNavItem(item);
      });
      const openNavItem = item => {
        const toggle = item.querySelector('[data-ei-submenu-toggle]');
        const submenu = toggle && document.getElementById(toggle.getAttribute('aria-controls'));
        if (!toggle || !submenu) return;
        closeNavItems(item);
        header.querySelectorAll('.ei-header-localization[open]').forEach(details => { details.open = false; });
        item.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        submenu.hidden = false;
      };
      navItems.forEach(item => {
        const toggle = item.querySelector('[data-ei-submenu-toggle]');
        toggle.addEventListener('click', event => {
          event.preventDefault();
          item.classList.contains('is-open') ? closeNavItem(item) : openNavItem(item);
        }, options);
      });
      header.querySelectorAll('.ei-desktop-nav [data-ei-nav-item]').forEach(item => {
        item.addEventListener('pointerenter', event => {
          if (event.pointerType === 'mouse') openNavItem(item);
        }, options);
        item.addEventListener('pointerleave', () => closeNavItem(item), options);
        item.addEventListener('focusin', () => {
          if (skipFocusOpen !== item) openNavItem(item);
        }, options);
        item.addEventListener('focusout', event => {
          if (!item.contains(event.relatedTarget)) closeNavItem(item);
        }, options);
      });
      header.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const item = event.target.closest('[data-ei-nav-item].is-open');
        if (item) { closeNavItem(item, true); event.preventDefault(); }
        const details = event.target.closest('details[open]');
        if (details) { details.open = false; details.querySelector('summary').focus(); event.preventDefault(); }
      }, options);
      document.addEventListener('click', event => {
        navItems.forEach(item => {
          if (!item.contains(event.target)) closeNavItem(item);
        });
        header.querySelectorAll('.ei-header-localization[open]').forEach(details => {
          if (!details.contains(event.target)) details.open = false;
        });
      }, options);
    });
    if ('IntersectionObserver' in window && !reducedMotion.matches && !window.Shopify?.designMode) {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) { entry.target.classList.add('ei-in'); observer.unobserve(entry.target); }
        });
      }, { threshold: .08 });
      container.querySelectorAll('.ei-reveal').forEach(element => {
        element.closest('.ei')?.classList.add('ei-motion');
        observer.observe(element);
      });
      observers.set(container, observer);
    }
    syncSaved();
  }
  document.addEventListener('click', event => {
    const button = event.target.closest('[data-ei-save]');
    if (!button) return;
    const id = button.dataset.eiSave;
    saved.has(id) ? saved.delete(id) : saved.add(id);
    try { localStorage.setItem('earth-iman-saved', JSON.stringify([...saved])); } catch { /* Session-only save when storage is unavailable. */ }
    syncSaved();
  });
  window.addEventListener('storage', event => { if (event.key === 'earth-iman-saved') { saved = readSaved(); syncSaved(); } });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    observers.get(event.target)?.disconnect();
    event.target.querySelectorAll('[data-ei-header], [data-ei-tabs], [data-ei-ritual]').forEach(element => {
      controllers.get(element)?.abort();
      controllers.delete(element);
    });
    if (event.target.querySelector('.ei-mobile-menu[open]')) document.documentElement.removeAttribute('scroll-lock');
  });
  document.addEventListener('shopify:block:select', event => {
    const root = event.target.closest('[data-ei-tabs], [data-ei-ritual]');
    if (root && event.target.matches('[role="tab"]')) selectTab(root, event.target);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
